import * as Sentry from '@sentry/react';

import { createCardTokenPagarmeV5 } from 'app/helpers/payment/cardTokenization';
import type { CreditCardValidationValues } from 'app/schemas/creditCardValidation';

// Módulo leve: helpers de pagamento compartilhados sem arrastar o useCheckoutSubmission (Redux, tracking, polling).
const TOKENIZATION_TIMEOUT_MS = 30 * 1000;

export const captureCheckoutException = (
  flow: string,
  error: unknown,
  extras: Record<string, unknown> = {},
): void => {
  Sentry.withScope((scope) => {
    scope.setTag('area', 'upgrade_subscription');
    scope.setTag('flow', flow);
    scope.setTag('provider', 'pagarme_v5');
    Object.entries(extras).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    const sanitized = new Error((error as { message?: string })?.message || 'Checkout failed');
    sanitized.name = (error as { name?: string })?.name || 'Error';
    Sentry.captureException(sanitized);
  });
};

export const tokenizeCardWithTimeout: typeof createCardTokenPagarmeV5 = (cardData) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Tempo excedido ao processar o cartão'));
    }, TOKENIZATION_TIMEOUT_MS);

    createCardTokenPagarmeV5(cardData).then(
      (result) => {
        clearTimeout(timer);
        resolve(result);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

export const splitExpiration = (cardExpiration: string): { expMonth: string; expYear: string } => {
  const digits = cardExpiration.replace(/\D/g, '');
  return { expMonth: digits.slice(0, 2), expYear: digits.slice(2, 4) };
};

export type JussubNewCardData = {
  card_token: string;
  lastFour: string;
  brand: string;
  expMonth: string;
  expYear: string;
  holderName: string;
};

export type PagarmeV5CardToken = Awaited<ReturnType<typeof createCardTokenPagarmeV5>> & {
  card?: {
    last_four_digits?: string;
    brand?: string;
    holder_name?: string;
    exp_month?: number | string;
    exp_year?: number | string;
  };
};

export const buildJussubNewCardData = (
  token: PagarmeV5CardToken,
  newCardValues: CreditCardValidationValues,
): JussubNewCardData => {
  const { expMonth, expYear } = splitExpiration(newCardValues.card_expiration);

  return {
    card_token: token.id,
    lastFour: token.card?.last_four_digits ?? newCardValues.card_number.replace(/\D/g, '').slice(-4),
    brand: token.card?.brand ?? '',
    expMonth: String(token.card?.exp_month ?? expMonth),
    expYear: String(token.card?.exp_year ?? expYear),
    holderName: token.card?.holder_name ?? newCardValues.cardholder_name,
  };
};
